import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { EmptyState } from "../../components/auction/FilterSection";
import { InvoicesModal } from "../../components/auction/InvoicesModal";
import { ManageAuctionCard } from "../../components/auction/ManageAuctionCard";
import type { UnifiedAuction } from "../../components/auction/AuctionCardShared";

export function MineAuksjonerPage() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<UnifiedAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<UnifiedAuction["invoices"] | null>(null);

  const fetchAuctions = () => {
    if (!organizationSlug) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    setLoading(true);
    fetch(`${api}/api/organizations/${organizationSlug}/auctions`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke laste auksjoner");
        return res.json() as Promise<any[]>;
      })
      .then((data) => {
        const mapped = data.map((a) => {
          const v = a.vehicle;
          const primaryImage = v?.images?.find((i: any) => i.isPrimary) || v?.images?.[0] || null;
          return {
            ...a,
            primaryImage,
            vehicle: v ? { ...v, images: undefined } : null,
          };
        });
        setAuctions(mapped);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Ukjent feil"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAuctions();
  }, [organizationSlug]);

  const pendingAction = auctions.filter(a =>
    a.status === "pending_seller_decision" || a.status === "negotiating" || a.status === "changes_requested" || a.status === "no_bids"
  );
  const otherAuctions = auctions.filter(a =>
    a.status !== "pending_seller_decision" && a.status !== "negotiating" && a.status !== "changes_requested" && a.status !== "no_bids"
  );

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 py-4 sm:py-6">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-black text-[#1a2e35]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Mine auksjoner
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {loading ? "Laster..." : <><strong>{auctions.length}</strong> auksjoner totalt</>}
          </p>
        </div>
        <button
          onClick={() => navigate(`/${organizationSlug}/mine-auksjoner/ny`)}
          className="flex flex-shrink-0 items-center gap-2 rounded-xl bg-accent px-5 py-3 text-sm font-bold text-white shadow-lg transition-all hover:scale-105 active:scale-95"
        >
          <Plus className="h-5 w-5" />
          Ny auksjon
        </button>
      </div>

      {error ? (
        <EmptyState message="Noe gikk galt" sub={error} />
      ) : loading ? (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : auctions.length === 0 ? (
        <EmptyState
          message="Ingen auksjoner ennå"
          sub="Du har ikke opprettet noen auksjoner. Klikk på 'Ny auksjon' for å komme i gang."
        />
      ) : (
        <div className="space-y-12">
          {pendingAction.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-accent animate-pulse" />
                <h2 className="text-xl font-black text-[#1a2e35]" style={{ fontFamily: "var(--font-display)" }}>
                  Trenger din handling
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {pendingAction.map((auction) => (
                  <ManageAuctionCard
                    key={auction.id}
                    auction={auction}
                    organizationSlug={organizationSlug!}
                    mode="seller"
                    onRefresh={fetchAuctions}
                  />
                ))}
              </div>
            </div>
          )}

          {otherAuctions.length > 0 && (
            <div className="space-y-6">
              {pendingAction.length > 0 && (
                <h2 className="text-xl font-black text-[#1a2e35]" style={{ fontFamily: "var(--font-display)" }}>
                  Andre auksjoner
                </h2>
              )}
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {otherAuctions.map((auction) => (
                  <ManageAuctionCard
                    key={auction.id}
                    auction={auction}
                    organizationSlug={organizationSlug!}
                    mode="seller"
                    onRefresh={fetchAuctions}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {selectedInvoices && (
        <InvoicesModal
          invoices={selectedInvoices}
          organizationSlug={organizationSlug!}
          onClose={() => setSelectedInvoices(null)}
        />
      )}
    </div>
  );
}
